import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Package, Plus, Save } from 'lucide-react';
import { vendorApi } from '../../api/vendorApi';
import { getVendorBrandOptions } from '../../lib/productBrands';

const EMPTY_PRODUCT = {
    name_fa: '', name_en: '', brand: '', description: '', base_price: '', cost_price: '',
    wholesale_price: '', wholesale_min_quantity: '',
    stock_quantity: '0', image_url: '', is_perishable: false,
    category_ids: [],
};

export default function VendorProductForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const [form, setForm] = useState(EMPTY_PRODUCT);
    const [loading, setLoading] = useState(isEditing);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [error, setError] = useState('');
    const [brands, setBrands] = useState([]);
    const [brandLoading, setBrandLoading] = useState(true);
    const [brandLoadError, setBrandLoadError] = useState('');
    const [isCreatingBrand, setIsCreatingBrand] = useState(false);

    useEffect(() => {
        let cancelled = false;
        vendorApi.getProducts()
            .then((products) => {
                if (!cancelled) setBrands(getVendorBrandOptions(products));
            })
            .catch((requestError) => {
                console.error(requestError);
                if (!cancelled) setBrandLoadError('بارگذاری برندهای قبلی ممکن نشد؛ می‌توانید نام برند را دستی وارد کنید.');
            })
            .finally(() => {
                if (!cancelled) setBrandLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (isEditing) {
            const fetchProduct = async () => {
                try {
                    const product = await vendorApi.getProduct(id);
                    setForm({
                        ...EMPTY_PRODUCT,
                        ...product,
                        brand: product.brand ?? '',
                        base_price: String(product.base_price ?? ''),
                        cost_price: product.cost_price != null ? String(product.cost_price) : '',
                        wholesale_price: product.wholesale_price != null ? String(product.wholesale_price) : '',
                        wholesale_min_quantity: product.wholesale_min_quantity != null ? String(product.wholesale_min_quantity) : '',
                        stock_quantity: String(product.stock_quantity ?? 0),
                        category_ids: product.categories?.map((c) => c.id) ?? [],
                    });
                } catch {
                    setError('خطا در دریافت اطلاعات محصول');
                } finally {
                    setLoading(false);
                }
            };
            fetchProduct();
        }
    }, [id, isEditing]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleImageChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploadingImage(true);
        setError('');
        try {
            const result = await vendorApi.uploadImage(file);
            setForm((current) => ({ ...current, image_url: result.url }));
        } catch {
            setError('بارگذاری تصویر با خطا مواجه شد');
        } finally {
            setUploadingImage(false);
            event.target.value = '';
        }
    };

    const saveProduct = async (event) => {
        event.preventDefault();
        setError('');

        const wholesalePrice = form.wholesale_price === '' ? null : Number(form.wholesale_price);
        const wholesaleMinQuantity = form.wholesale_min_quantity === '' ? null : Number(form.wholesale_min_quantity);
        const basePrice = Number(form.base_price);

        if ((wholesalePrice === null) !== (wholesaleMinQuantity === null)) {
            setError('قیمت عمده و حداقل تعداد باید همزمان وارد شوند.');
            return;
        }
        if (wholesalePrice !== null && wholesalePrice >= basePrice) {
            setError('قیمت عمده باید کمتر از قیمت پایه باشد.');
            return;
        }
        const costPrice = form.cost_price === '' ? null : Number(form.cost_price);
        if (costPrice !== null) {
            if (!Number.isFinite(costPrice) || costPrice <= 0) {
                setError('قیمت تمام‌شده باید عددی بزرگ‌تر از صفر باشد.');
                return;
            }
            if (costPrice >= basePrice) {
                setError('قیمت تمام‌شده باید کمتر از قیمت پایه باشد.');
                return;
            }
        }

        setSaving(true);
        const finalNameEn = form.name_en.trim() || form.name_fa.trim();

        const payload = {
            ...form,
            name_en: finalNameEn,
            brand: form.brand.trim() || null,
            base_price: basePrice,
            cost_price: costPrice,
            wholesale_price: wholesalePrice,
            wholesale_min_quantity: wholesaleMinQuantity,
            stock_quantity: Number(form.stock_quantity),
        };
        delete payload.is_kalabarg_eligible;

        try {
            if (isEditing) await vendorApi.updateProduct(id, payload);
            else await vendorApi.createProduct(payload);
            navigate('/vendor/products');
        } catch {
            setError('ذخیره محصول با خطا مواجه شد');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '2rem' }}>در حال دریافت اطلاعات...</div>;

    const inputStyle = { width: '100%', padding: '0.45rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', outline: 'none' };
    const labelStyle = { display: 'block', marginBottom: '0.3rem', fontSize: '0.8rem', fontWeight: 'bold', color: '#334155' };
    const currentBrand = typeof form.brand === 'string' ? form.brand.trim() : '';
    const availableBrands = [...new Set([...brands, currentBrand].filter(Boolean))];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* هدر صفحه */}
            <div className="admin-page-header" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Package size={22} color="#2563eb" />
                    <h1 className="admin-page-title" style={{ margin: 0 }}>
                        {isEditing ? 'ویرایش محصول' : 'افزودن محصول جدید'}
                    </h1>
                </div>
                <button onClick={() => navigate('/vendor/products')} className="admin-refresh-btn" style={{ background: '#2563eb', color: '#fff' }}>
                    بازگشت به لیست محصولات
                </button>
            </div>

            {error && <div className="admin-table-error"><AlertCircle size={16} />{error}</div>}

            <form onSubmit={saveProduct} className="card admin-product-form">

                {/* ردیف اول */}
                <div className="vendor-product-primary-grid">
                    <div>
                        <label style={labelStyle}>نام فارسی *</label>
                        <input name="name_fa" value={form.name_fa} onChange={handleChange} required style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>نام لاتین</label>
                        <input name="name_en" value={form.name_en} onChange={handleChange} style={{ ...inputStyle, direction: 'ltr' }} />
                    </div>
                    <div>
                        <label htmlFor={isCreatingBrand ? 'new-product-brand' : 'product-brand'} style={labelStyle}>برند</label>
                        <div className="vendor-product-brand-control">
                            {isCreatingBrand ? (
                                <>
                                    <input
                                        id="new-product-brand"
                                        name="brand"
                                        value={form.brand}
                                        onChange={handleChange}
                                        placeholder="نام برند جدید"
                                        autoComplete="organization"
                                        aria-describedby={brandLoadError
                                            ? 'product-brand-hint product-brand-load-error'
                                            : 'product-brand-hint'}
                                        style={inputStyle}
                                    />
                                    <button
                                        type="button"
                                        className="vendor-product-brand-action"
                                        onClick={() => {
                                            setIsCreatingBrand(false);
                                            setForm((current) => ({ ...current, brand: '' }));
                                        }}
                                    >
                                        انتخاب برند قبلی
                                    </button>
                                </>
                            ) : (
                                <>
                                    <select
                                        id="product-brand"
                                        name="brand"
                                        value={form.brand}
                                        onChange={handleChange}
                                        aria-describedby={brandLoadError
                                            ? 'product-brand-hint product-brand-load-error'
                                            : 'product-brand-hint'}
                                        style={inputStyle}
                                    >
                                        <option value="">بدون برند</option>
                                        {availableBrands.map((brand) => (
                                            <option key={brand} value={brand}>{brand}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="vendor-product-brand-action"
                                        onClick={() => {
                                            setForm((current) => ({ ...current, brand: '' }));
                                            setIsCreatingBrand(true);
                                        }}
                                    >
                                        <Plus size={16} aria-hidden="true" />
                                        برند جدید
                                    </button>
                                </>
                            )}
                        </div>
                        <small id="product-brand-hint" className="vendor-product-brand-hint">
                            {brandLoading
                                ? 'در حال دریافت برندهای محصولات شما…'
                                : isCreatingBrand
                                    ? 'برند تازه پس از ذخیرهٔ محصول به فهرست برندهای شما اضافه می‌شود.'
                                    : 'برندهای استفاده‌شده در محصولات فروشگاه شما'}
                        </small>
                        {brandLoadError && (
                            <small id="product-brand-load-error" className="vendor-product-brand-error" role="status">{brandLoadError}</small>
                        )}
                    </div>
                    <div>
                        <label style={labelStyle}>قیمت پایه (ریال) *</label>
                        <input name="base_price" type="number" min="0" step="0.01" value={form.base_price} onChange={handleChange} required style={inputStyle} />
                    </div>
                    <div>
                        <label htmlFor="product-cost-price" style={labelStyle}>قیمت تمام‌شده (ریال)</label>
                        <input
                            id="product-cost-price"
                            name="cost_price"
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.cost_price}
                            onChange={handleChange}
                            style={inputStyle}
                        />
                        <small style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.7rem', color: '#64748b' }}>
                            بهای تمام‌شدهٔ محصول برای محاسبهٔ سود در خرید گروهی؛ اختیاری
                        </small>
                    </div>
                </div>

                {/* ردیف دوم */}
                <div className="vendor-product-secondary-grid">
                    <div>
                        <label style={labelStyle}>موجودی *</label>
                        <input name="stock_quantity" type="number" min="0" value={form.stock_quantity} onChange={handleChange} required style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>قیمت عمده</label>
                        <input name="wholesale_price" type="number" min="0" step="0.01" value={form.wholesale_price} onChange={handleChange} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>حداقل تعداد عمده</label>
                        <input name="wholesale_min_quantity" type="number" min="1" step="1" value={form.wholesale_min_quantity} onChange={handleChange} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>توضیحات</label>
                        <textarea name="description" value={form.description} onChange={handleChange} rows="1" style={{ ...inputStyle, resize: 'vertical', minHeight: '36px' }} />
                    </div>
                </div>

                {/* بخش تصویر و چک‌باکس */}
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', background: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {form.image_url && (
                            <img src={form.image_url} alt="Preview" style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                        )}
                        <div>
                            <label style={{ cursor: 'pointer', background: '#2563eb', color: '#fff', padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', display: 'inline-block' }}>
                                {uploadingImage ? 'در حال بارگذاری...' : 'انتخاب تصویر'}
                                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} disabled={uploadingImage} style={{ display: 'none' }} />
                            </label>
                        </div>
                    </div>

                    <div style={{ width: '1px', height: '30px', background: '#cbd5e1' }}></div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input type="checkbox" name="is_perishable" checked={form.is_perishable} onChange={handleChange} id="perishable" style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        <label htmlFor="perishable" style={{ cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold', color: '#334155' }}>این محصول فاسدشدنی است</label>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="submit" className="admin-refresh-btn" disabled={saving || uploadingImage} style={{ background: '#10b981', color: '#fff', padding: '0.5rem 1.5rem' }}>
                        <Save size={16} /> {saving ? 'در حال ذخیره...' : 'ذخیره محصول'}
                    </button>
                </div>

            </form>
        </div>
    );
}